/**
 * Auth Store - 认证状态管理
 *
 * 管理用户认证状态、登录、登出等功能
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { setAuthToken, clearAuthToken, setUnauthorizedCallback } from '@/api/request'
import type { User } from '@/types/auth'

/** localStorage 存储 key */
const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'

/**
 * 解码 JWT token 的 payload 部分
 *
 * @param token - JWT token 字符串
 * @returns 解码后的 payload 对象，解码失败返回 null
 */
function decodeJwtPayload(token: string): { exp?: number; sub?: string } | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = parts[1]
    // Base64URL 解码
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

/**
 * 检查 JWT token 是否已过期
 *
 * @param token - JWT token 字符串
 * @returns true 表示已过期或无效，false 表示有效
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload || !payload.exp) return true

  // exp 是 Unix 时间戳（秒）
  const expirationTime = payload.exp * 1000
  const now = Date.now()

  // 提前 60 秒判定为过期，避免边界情况
  return now >= expirationTime - 60000
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // Actions

  /**
   * 处理 401 未授权响应 - 自动登出
   */
  function handleUnauthorized(): void {
    // 清除状态
    token.value = null
    user.value = null
    error.value = '登录已过期，请重新登录'
    clearAuthToken()

    // 清除 localStorage
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }

  /**
   * 初始化认证状态（从 localStorage 恢复）
   *
   * @returns true 表示成功恢复有效的认证状态
   */
  function initialize(): boolean {
    // 注册 401 回调
    setUnauthorizedCallback(handleUnauthorized)

    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken) {
      // 检查 token 是否过期
      if (isTokenExpired(storedToken)) {
        // token 已过期，清除存储
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(USER_KEY)
        return false
      }

      token.value = storedToken
      setAuthToken(storedToken)

      if (storedUser) {
        try {
          user.value = JSON.parse(storedUser)
        } catch {
          // 解析失败，清除无效数据
          localStorage.removeItem(USER_KEY)
        }
      }
      return true
    }
    return false
  }

  /**
   * 用户登录
   *
   * @param username - 用户名
   * @param password - 密码
   */
  async function login(username: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      // 调用登录 API
      const tokenResponse = await authApi.login(username, password)

      // 保存 token
      token.value = tokenResponse.access_token
      localStorage.setItem(TOKEN_KEY, tokenResponse.access_token)

      // 获取用户信息
      try {
        const currentUser = await authApi.getCurrentUser()
        user.value = currentUser
        localStorage.setItem(USER_KEY, JSON.stringify(currentUser))
      } catch {
        // 获取用户信息失败，仍视为登录成功
        // 用户信息可以稍后获取
      }

      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : '登录失败'
      token.value = null
      user.value = null
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 用户登出
   */
  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // 忽略登出 API 错误
    } finally {
      // 清除状态
      token.value = null
      user.value = null
      error.value = null
      clearAuthToken()

      // 清除 localStorage
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
    }
  }

  /**
   * 刷新用户信息
   */
  async function refreshUser(): Promise<void> {
    if (!token.value) return

    try {
      const currentUser = await authApi.getCurrentUser()
      user.value = currentUser
      localStorage.setItem(USER_KEY, JSON.stringify(currentUser))
    } catch {
      // 获取失败可能是 token 过期
      await logout()
    }
  }

  /**
   * 清除错误
   */
  function clearError(): void {
    error.value = null
  }

  /**
   * 检查 token 是否有效
   *
   * @returns true 表示 token 存在且未过期
   */
  function checkTokenValidity(): boolean {
    if (!token.value) return false
    return !isTokenExpired(token.value)
  }

  return {
    // State
    user,
    token,
    isLoading,
    error,

    // Getters
    isAuthenticated,

    // Actions
    initialize,
    login,
    logout,
    refreshUser,
    clearError,
    checkTokenValidity,
  }
})
